import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that represents a single cart item.
 * Handles item-specific interactions like quantity changes, removal, and animations.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} quantitySelector - The quantity selector element.
 * @property {HTMLElement} errorContainer - The error message container.
 * @property {HTMLElement} errorText - The error message text element.
 * @property {HTMLElement} linePrice - The line price text component.
 *
 * @extends {Component<Refs>}
 */
class CartItem extends Component {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange);

    // Listen for frequency dropdown updates
    this.addEventListener('frequency-dropdown-updated', this.#handleFrequencyDropdownUpdate);

    // Initial update (with a small delay to ensure dropdown is rendered)
    setTimeout(() => this.#updateSubscriptionText(), 200);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange);
    this.removeEventListener('frequency-dropdown-updated', this.#handleFrequencyDropdownUpdate);
  }

  /**
   * Handles frequency dropdown update events.
   * @param {CustomEvent} event - The frequency dropdown update event.
   */
  #handleFrequencyDropdownUpdate = (event) => {
    this.#updateSubscriptionText();
  };

  /**
   * Updates the subscription text display based on the frequency dropdown value.
   */
  #updateSubscriptionText() {
    const frequencyDropdown = this.querySelector('cart-item-frequency-dropdown');
    const subscriptionTextEl = /** @type {HTMLElement | null} */ (
      this.querySelector('.cart-items__subscription-text')
    );
    const subscriptionSaveEl = this.querySelector('.cart-items__save-percent-text');

    if (!subscriptionTextEl) {
      console.log('No subscription text element found');
      return;
    }

    if (!subscriptionSaveEl) {
      console.log('No save percent element found');
      return;
    }

    if (!frequencyDropdown) {
      console.log('No frequency dropdown found - item is not a subscription');
      subscriptionTextEl.style.display = 'none';
      return;
    }

    console.log('Updating subscription text...');

    // Get the selected frequency from the dropdown button in shadow DOM
    const shadowRoot = frequencyDropdown.shadowRoot;

    if (!shadowRoot) {
      // Shadow DOM might not be ready yet, try again after a delay
      setTimeout(() => this.#updateSubscriptionText(), 100);
      return;
    }

    const dropdownButton = shadowRoot.querySelector('.DropdownOption__button');

    if (!dropdownButton) {
      console.error('No dropdown button found');
      return;
    }

    const frequencyText = dropdownButton.textContent?.trim() || '';

    // Extract the number of months from text like "Delivery every 1 month"
    const monthsMatch = frequencyText.match(/(\d+)\s*month/i);
    const months = monthsMatch ? monthsMatch[1] : '1';

    // Get the subscription discount percentage from the data attribute
    const discountPercent = this.dataset.subscriptionDiscount || '10';

    // Update the subscription text with / prefix
    const finalText = ` / delivery every ${months} month${months !== '1' ? 's' : ''}`;
    subscriptionTextEl.textContent = finalText;
    subscriptionTextEl.style.display = 'inline';
    subscriptionSaveEl.textContent = `(SAVE ${discountPercent}%)`;
  }

  /**
   * Handles quantity selector update events.
   * @param {QuantitySelectorUpdateEvent} event - The quantity selector event.
   */
  #handleQuantityChange = (event) => {
    const { cartLine } = event.detail;

    // Only handle events for this cart item
    if (cartLine !== this.lineIndex) return;

    const { quantity } = event.detail;

    if (quantity === 0) {
      this.remove();
    } else {
      this.shimmerPrice();
    }
  };

  /**
   * Starts the removal animation without actually removing the element.
   * The section re-render will handle the actual removal from DOM.
   */
  startRemovalAnimation() {
    if (prefersReducedMotion()) {
      return;
    }

    // Trigger remove animation
    this.style.setProperty('--row-height', `${this.clientHeight}px`);
    this.classList.add('removing');
  }

  /**
   * Adds shimmer effect to the line price.
   */
  shimmerPrice() {
    const { linePrice } = this.refs;
    const textComponent = /** @type {any} */ (linePrice);
    if (textComponent && typeof textComponent.shimmer === 'function') {
      textComponent.shimmer();
    }
  }

  /**
   * Displays an error message for this cart item.
   * @param {string} errorMessage - The error message to display.
   */
  showError(errorMessage) {
    const { errorContainer, errorText } = this.refs;

    if (errorText instanceof HTMLElement) {
      errorText.textContent = errorMessage;
    }

    if (errorContainer instanceof HTMLElement) {
      errorContainer.classList.remove('hidden');
    }
  }

  /**
   * Hides the error message for this cart item.
   */
  hideError() {
    const { errorContainer } = this.refs;

    if (errorContainer instanceof HTMLElement) {
      errorContainer.classList.add('hidden');
    }
  }

  /**
   * Gets the line index (1-based) for this cart item.
   * @returns {number} The line index.
   */
  get lineIndex() {
    const lineIndex = parseInt(this.dataset.lineIndex || '0', 10);
    if (!lineIndex) {
      console.error('Cart item missing data-line-index attribute');
    }
    return lineIndex;
  }

  /**
   * Gets the cart item key.
   * @returns {string} The cart item key.
   */
  get itemKey() {
    return this.dataset.itemKey || '';
  }

  /**
   * Gets the product handle.
   * @returns {string} The product handle.
   */
  get productHandle() {
    return this.dataset.productHandle || '';
  }

  /**
   * Gets the variant ID.
   * @returns {string} The variant ID.
   */
  get variantId() {
    return this.dataset.variantId || '';
  }

  /**
   * Gets the current quantity.
   * @returns {number} The current quantity.
   */
  get quantity() {
    return parseInt(this.dataset.quantity || '0', 10);
  }
}

if (!customElements.get('cart-item')) {
  customElements.define('cart-item', CartItem);
}
